import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLatticeDeform, type DragScope } from "./LatticeDeformContext";
import { extractSvgInnerContent } from "../../utils/channelSplitVector";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

interface DragInfo {
    pointerId: number;
    i: number;
    j: number;
    scope: DragScope;
    /** Drag origin in viewBox space (used for axis-lock target calc). */
    startLocalX: number;
    startLocalY: number;
    /** Cumulative delta already emitted to `movePoint` since drag start. */
    appliedDx: number;
    appliedDy: number;
    /** Whether axis lock is active for this drag (captured on pointer down). */
    lockAxis: boolean;
    /** Locked axis once the user has moved past the threshold. */
    lockedAxis: "x" | "y" | null;
}

export function LatticeDeformViewer() {
    const {
        svg,
        warpedSvgContent,
        canExport,
        handleDownload,
        vb,
        cols,
        rows,
        lattice,
        movePoint,
        resetNode,
        showLattice,
        lockAxis,
        pushLatticeSnapshot,
        undo,
        redo,
    } = useLatticeDeform();

    useUndoRedoKeyboard({ undo, redo });

    const base = useMemo(
        () => ({ x: vb.x, y: vb.y, w: vb.width, h: vb.height }),
        [vb.x, vb.y, vb.width, vb.height],
    );

    const {
        svgRef,
        viewBoxAttr: viewBox,
        view,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const dragRef = useRef<DragInfo | null>(null);
    const pendingDelta = useRef<{ dx: number; dy: number } | null>(null);
    const rafHandle = useRef<number | null>(null);
    /** Active drag target + scope (drives handle styling while dragging). */
    const [dragState, setDragState] = useState<{
        i: number;
        j: number;
        scope: DragScope;
    } | null>(null);
    /** Hovered lattice node (pointer over handle, not dragging). */
    const [hovered, setHovered] = useState<{ i: number; j: number } | null>(null);
    const [shiftDown, setShiftDown] = useState(false);
    const [altDown, setAltDown] = useState(false);

    const innerWarped = useMemo(() => {
        if (!warpedSvgContent) return null;
        return extractSvgInnerContent(warpedSvgContent);
    }, [warpedSvgContent]);

    // Rough handle size in source units (~1% of the viewBox diagonal, with
    // sensible min/max). Scaled to compensate for the live zoom (base.w / view.w)
    // so the handles stay roughly screen-constant.
    const handleRadius = useMemo(() => {
        const diag = Math.sqrt(vb.width * vb.width + vb.height * vb.height);
        const baseR = Math.max(diag * 0.008, 1);
        const scale = base.w / view.w;
        return baseR / Math.max(scale, 0.1);
    }, [vb.width, vb.height, base.w, view.w]);

    const meshStrokeWidth = useMemo(() => handleRadius * 0.4, [handleRadius]);

    useEffect(() => {
        const syncMods = (e: KeyboardEvent) => {
            setShiftDown(e.shiftKey);
            setAltDown(e.altKey);
        };
        const clearMods = () => {
            setShiftDown(false);
            setAltDown(false);
        };
        window.addEventListener("keydown", syncMods);
        window.addEventListener("keyup", syncMods);
        window.addEventListener("blur", clearMods);
        return () => {
            window.removeEventListener("keydown", syncMods);
            window.removeEventListener("keyup", syncMods);
            window.removeEventListener("blur", clearMods);
        };
    }, []);

    const flushPendingDelta = useCallback(() => {
        rafHandle.current = null;
        const drag = dragRef.current;
        const delta = pendingDelta.current;
        if (!drag || !delta) return;
        pendingDelta.current = null;
        if (delta.dx === 0 && delta.dy === 0) return;
        movePoint(drag.i, drag.j, delta.dx, delta.dy, drag.scope);
    }, [movePoint]);

    const queueDelta = useCallback((dx: number, dy: number) => {
        if (!pendingDelta.current) pendingDelta.current = { dx: 0, dy: 0 };
        pendingDelta.current.dx += dx;
        pendingDelta.current.dy += dy;
        if (rafHandle.current == null) {
            rafHandle.current = requestAnimationFrame(flushPendingDelta);
        }
    }, [flushPendingDelta]);

    const clientToLocal = useCallback((clientX: number, clientY: number) => {
        const el = svgRef.current;
        if (!el) return null;
        const ctm = el.getScreenCTM();
        if (!ctm) return null;
        const pt = el.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const inv = ctm.inverse();
        const p = pt.matrixTransform(inv);
        return { x: p.x, y: p.y };
    }, [svgRef]);

    const handlePointerDown = useCallback(
        (event: React.PointerEvent<SVGCircleElement>, i: number, j: number) => {
            event.preventDefault();
            event.stopPropagation();
            const local = clientToLocal(event.clientX, event.clientY);
            if (!local) return;
            pushLatticeSnapshot();
            const scope: DragScope = event.shiftKey
                ? "row"
                : event.altKey
                    ? "column"
                    : "single";
            dragRef.current = {
                pointerId: event.pointerId,
                i,
                j,
                scope,
                startLocalX: local.x,
                startLocalY: local.y,
                appliedDx: 0,
                appliedDy: 0,
                lockAxis,
                lockedAxis: null,
            };
            setDragState({ i, j, scope });
            setHovered(null);
            (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
        },
        [clientToLocal, lockAxis, pushLatticeSnapshot],
    );

    /**
     * Threshold (in viewBox units) for committing to an axis when
     * axis-lock is enabled. Tied to the viewBox diagonal so it feels
     * the same across very large / very small artworks.
     */
    const axisLockThreshold = useMemo(
        () => Math.max(1e-6, Math.hypot(vb.width, vb.height) * 0.005),
        [vb.width, vb.height],
    );

    const handlePointerMove = useCallback(
        (event: React.PointerEvent<SVGCircleElement>) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) return;
            const local = clientToLocal(event.clientX, event.clientY);
            if (!local) return;

            const rawDx = local.x - drag.startLocalX;
            const rawDy = local.y - drag.startLocalY;

            let targetDx = rawDx;
            let targetDy = rawDy;
            if (drag.lockAxis) {
                if (drag.lockedAxis == null) {
                    if (
                        Math.abs(rawDx) > axisLockThreshold ||
                        Math.abs(rawDy) > axisLockThreshold
                    ) {
                        drag.lockedAxis =
                            Math.abs(rawDx) >= Math.abs(rawDy) ? "x" : "y";
                    }
                }
                if (drag.lockedAxis === "x") targetDy = 0;
                else if (drag.lockedAxis === "y") targetDx = 0;
                else { targetDx = 0; targetDy = 0; }
            }

            const dx = targetDx - drag.appliedDx;
            const dy = targetDy - drag.appliedDy;
            drag.appliedDx = targetDx;
            drag.appliedDy = targetDy;
            if (dx !== 0 || dy !== 0) queueDelta(dx, dy);
        },
        [clientToLocal, queueDelta, axisLockThreshold],
    );

    const endDrag = useCallback((event: React.PointerEvent<SVGCircleElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        if (rafHandle.current != null) {
            cancelAnimationFrame(rafHandle.current);
            rafHandle.current = null;
            flushPendingDelta();
        }
        try {
            (event.currentTarget as Element).releasePointerCapture?.(event.pointerId);
        } catch {
            /* pointer already released by browser */
        }
        dragRef.current = null;
        setDragState(null);
    }, [flushPendingDelta]);

    const handleDoubleClick = useCallback(
        (event: React.MouseEvent<SVGCircleElement>, i: number, j: number) => {
            event.preventDefault();
            event.stopPropagation();
            resetNode(i, j);
        },
        [resetNode],
    );

    useEffect(() => () => {
        if (rafHandle.current != null) cancelAnimationFrame(rafHandle.current);
    }, []);

    // ── Mesh polyline strings (rows + columns) ────────────────────────────────
    const meshLines = useMemo(() => {
        const out: string[] = [];
        // Horizontal lines (per row j)
        for (let j = 0; j <= rows; j++) {
            const row = lattice[j];
            if (!row) continue;
            out.push(row.map((p) => `${p.x},${p.y}`).join(" "));
        }
        // Vertical lines (per column i)
        for (let i = 0; i <= cols; i++) {
            const segs: string[] = [];
            for (let j = 0; j <= rows; j++) {
                const p = lattice[j]?.[i];
                if (!p) continue;
                segs.push(`${p.x},${p.y}`);
            }
            out.push(segs.join(" "));
        }
        return out;
    }, [lattice, cols, rows]);

    const hoveredRowPolyline = useMemo(() => {
        if (!hovered || dragState || !shiftDown) return null;
        const row = lattice[hovered.j];
        if (!row) return null;
        return row.map((p) => `${p.x},${p.y}`).join(" ");
    }, [hovered, dragState, shiftDown, lattice]);

    const hoveredColPolyline = useMemo(() => {
        if (!hovered || dragState || !altDown || shiftDown) return null;
        const segs: string[] = [];
        for (let j = 0; j <= rows; j++) {
            const p = lattice[j]?.[hovered.i];
            if (p) segs.push(`${p.x},${p.y}`);
        }
        return segs.length ? segs.join(" ") : null;
    }, [hovered, dragState, shiftDown, altDown, lattice, rows]);

    const getNodeVisual = useCallback(
        (i: number, j: number) => {
            if (dragState) {
                const { i: di, j: dj, scope } = dragState;
                if (scope === "single" && i === di && j === dj) {
                    return { fill: "#1d4ed8", stroke: "#ffffff", rMul: 1.35 };
                }
                if (scope === "row" && j === dj) {
                    return { fill: "#059669", stroke: "#ecfdf5", rMul: 1.2 };
                }
                if (scope === "column" && i === di) {
                    return { fill: "#d97706", stroke: "#fffbeb", rMul: 1.2 };
                }
                return { fill: "#93c5fd", stroke: "#ffffff", rMul: 1 };
            }
            if (hovered) {
                const rowPick = shiftDown && !altDown;
                const colPick = altDown && !shiftDown;
                const both = shiftDown && altDown;
                if (both && j === hovered.j) {
                    return { fill: "#34d399", stroke: "#ecfdf5", rMul: i === hovered.i ? 1.25 : 1.12 };
                }
                if (rowPick && j === hovered.j) {
                    return {
                        fill: i === hovered.i ? "#10b981" : "#34d399",
                        stroke: "#ecfdf5",
                        rMul: i === hovered.i ? 1.25 : 1.12,
                    };
                }
                if (colPick && i === hovered.i) {
                    return {
                        fill: j === hovered.j ? "#f59e0b" : "#fbbf24",
                        stroke: "#fffbeb",
                        rMul: j === hovered.j ? 1.25 : 1.12,
                    };
                }
                if (i === hovered.i && j === hovered.j) {
                    return { fill: "#38bdf8", stroke: "#ffffff", rMul: 1.2 };
                }
            }
            return { fill: "#3b82f6", stroke: "#ffffff", rMul: 1 };
        },
        [dragState, hovered, shiftDown, altDown],
    );

    return (
        <div className="panel-viewer lattice-deform-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button type="button" className="zoom-button" onClick={zoomOut} title="Zoom out">−</button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button type="button" className="zoom-button" onClick={zoomIn} title="Zoom in">+</button>
                    <button type="button" className="zoom-button" onClick={resetView} title="Reset zoom">⌂</button>
                    {canExport && (
                        <button type="button" className="zoom-button" onClick={handleDownload} title="Download SVG">↓</button>
                    )}
                </div>

                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill lattice-deform-svg-container">
                        {innerWarped ? (
                            <svg
                                ref={svgRef}
                                viewBox={viewBox}
                                xmlns="http://www.w3.org/2000/svg"
                                className="path-tool-svg lattice-deform-svg-host"
                                style={{ width: "100%", height: "100%", touchAction: "none" }}
                                {...panHandlers}
                            >
                                <g
                                    className="lattice-deform-content"
                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: warped SVG markup is generated by our own pipeline
                                    dangerouslySetInnerHTML={{ __html: innerWarped }}
                                />
                                {showLattice && (
                                <g className="lattice-deform-overlay" pointerEvents="visiblePainted">
                                    {meshLines.map((pts, idx) => (
                                        <polyline
                                            key={`mesh-${idx}`}
                                            points={pts}
                                            fill="none"
                                            stroke="#3b82f6"
                                            strokeOpacity={0.45}
                                            strokeWidth={meshStrokeWidth}
                                            strokeDasharray={`${meshStrokeWidth * 4} ${meshStrokeWidth * 3}`}
                                            pointerEvents="none"
                                        />
                                    ))}
                                    {hoveredRowPolyline && (
                                        <polyline
                                            points={hoveredRowPolyline}
                                            fill="none"
                                            stroke="#10b981"
                                            strokeOpacity={0.85}
                                            strokeWidth={meshStrokeWidth * 1.75}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            pointerEvents="none"
                                        />
                                    )}
                                    {hoveredColPolyline && (
                                        <polyline
                                            points={hoveredColPolyline}
                                            fill="none"
                                            stroke="#f59e0b"
                                            strokeOpacity={0.85}
                                            strokeWidth={meshStrokeWidth * 1.75}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            pointerEvents="none"
                                        />
                                    )}
                                    {lattice.map((row, j) =>
                                        row.map((p, i) => {
                                            const vis = getNodeVisual(i, j);
                                            return (
                                                <circle
                                                    key={`node-${i}-${j}`}
                                                    cx={p.x}
                                                    cy={p.y}
                                                    r={handleRadius * vis.rMul}
                                                    fill={vis.fill}
                                                    stroke={vis.stroke}
                                                    strokeWidth={meshStrokeWidth}
                                                    style={{ cursor: "grab", touchAction: "none" }}
                                                    onPointerEnter={() => setHovered({ i, j })}
                                                    onPointerLeave={() =>
                                                        setHovered((h) =>
                                                            h?.i === i && h.j === j ? null : h,
                                                        )
                                                    }
                                                    onPointerDown={(e) => handlePointerDown(e, i, j)}
                                                    onPointerMove={handlePointerMove}
                                                    onPointerUp={endDrag}
                                                    onPointerCancel={endDrag}
                                                    onDoubleClick={(e) => handleDoubleClick(e, i, j)}
                                                />
                                            );
                                        }),
                                    )}
                                </g>
                                )}
                            </svg>
                        ) : (
                            <div className="svg-placeholder">
                                <p>Import an SVG to start deforming.</p>
                                <p className="svg-placeholder-hint">
                                    Drag mesh points to warp the artwork. Hold
                                    Shift to drag a whole row, Alt for a whole
                                    column. Double-click a point to reset it.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {svg && (
                    <div className="path-tool-footer">
                        <p className="path-tool-meta">
                            {cols} x {rows} cells - drag points - Shift = row - Alt = column - double-click = reset
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
